// Admin API client — all calls hit /api/admin/* and require IsAdminUser on
// the backend. Mirrors the JWT/Bearer pattern from taskService.js so the
// existing localStorage('accessToken') keeps working unchanged.

import axios from 'axios';
import API_BASE_URL from '../config';

const BASE_URL = `${API_BASE_URL}/api/admin`;

function authHeaders(extra = {}) {
  const token = localStorage.getItem('accessToken');
  return {
    Authorization: `Bearer ${token || ''}`,
    'Content-Type': 'application/json',
    ...extra,
  };
}

function handle401(err) {
  if (err?.response?.status === 401) {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    window.location.href = '/login';
  }
  throw err;
}

// ---------- analytics ----------
export async function getAnalytics() {
  try {
    const res = await axios.get(`${BASE_URL}/analytics/`, { headers: authHeaders() });
    return res.data;
  } catch (e) { handle401(e); }
}

// ---------- users ----------
export async function listUsers({ q = '', role = '', page = 1, pageSize = 50 } = {}) {
  const params = new URLSearchParams();
  if (q) params.set('q', q);
  if (role) params.set('role', role);
  params.set('page', page);
  params.set('page_size', pageSize);
  try {
    const res = await axios.get(`${BASE_URL}/users/?${params}`, { headers: authHeaders() });
    return res.data;
  } catch (e) { handle401(e); }
}

export async function getUser(userId) {
  try {
    const res = await axios.get(`${BASE_URL}/users/${userId}/`, { headers: authHeaders() });
    return res.data;
  } catch (e) { handle401(e); }
}

export async function updateUser(userId, patch) {
  try {
    const res = await axios.patch(`${BASE_URL}/users/${userId}/`, patch, { headers: authHeaders() });
    return res.data;
  } catch (e) { handle401(e); }
}

export async function deactivateUser(userId) {
  try {
    const res = await axios.post(`${BASE_URL}/users/${userId}/deactivate/`, {}, { headers: authHeaders() });
    return res.data;
  } catch (e) { handle401(e); }
}

// ---------- stores ----------
export async function listStores({ q = '', page = 1, pageSize = 50 } = {}) {
  const params = new URLSearchParams();
  if (q) params.set('q', q);
  params.set('page', page);
  params.set('page_size', pageSize);
  try {
    const res = await axios.get(`${BASE_URL}/stores/?${params}`, { headers: authHeaders() });
    return res.data;
  } catch (e) { handle401(e); }
}

export async function createStore(body) {
  try {
    const res = await axios.post(`${BASE_URL}/stores/`, body, { headers: authHeaders() });
    return res.data;
  } catch (e) { handle401(e); }
}

export async function updateStore(storeId, patch) {
  try {
    const res = await axios.patch(`${BASE_URL}/stores/${storeId}/`, patch, { headers: authHeaders() });
    return res.data;
  } catch (e) { handle401(e); }
}

export async function deleteStore(storeId) {
  try {
    const res = await axios.delete(`${BASE_URL}/stores/${storeId}/`, { headers: authHeaders() });
    return res.data;
  } catch (e) { handle401(e); }
}

// ---------- merchants & subscriptions ----------
export async function listMerchants({ page = 1, pageSize = 50 } = {}) {
  const params = new URLSearchParams({ page, page_size: pageSize });
  try {
    const res = await axios.get(`${BASE_URL}/merchants/?${params}`, { headers: authHeaders() });
    return res.data;
  } catch (e) { handle401(e); }
}

export async function getSubscription(storeId) {
  try {
    const res = await axios.get(`${BASE_URL}/subscriptions/${storeId}/`, { headers: authHeaders() });
    return res.data;
  } catch (e) { handle401(e); }
}

export async function upsertSubscription(storeId, body) {
  try {
    const res = await axios.post(`${BASE_URL}/subscriptions/${storeId}/`, body, { headers: authHeaders() });
    return res.data;
  } catch (e) { handle401(e); }
}

// ---------- task moderation ----------
export async function listTasks({ q = '', user_id = '', category = '', page = 1, pageSize = 50 } = {}) {
  const params = new URLSearchParams();
  if (q) params.set('q', q);
  if (user_id) params.set('user_id', user_id);
  if (category) params.set('category', category);
  params.set('page', page);
  params.set('page_size', pageSize);
  try {
    const res = await axios.get(`${BASE_URL}/tasks/?${params}`, { headers: authHeaders() });
    return res.data;
  } catch (e) { handle401(e); }
}

export async function deleteTask(taskId) {
  try {
    const res = await axios.delete(`${BASE_URL}/tasks/${taskId}/`, { headers: authHeaders() });
    return res.data;
  } catch (e) { handle401(e); }
}

// ---------- coupons (admin) ----------
// Wraps the existing /api/admin/coupons/ + /api/admin/coupons/<id>/ endpoints.
export async function listAdminCoupons() {
  try {
    const res = await axios.get(`${BASE_URL}/coupons/`, { headers: authHeaders() });
    return res.data;
  } catch (e) { handle401(e); }
}

export async function createAdminCoupon(body) {
  try {
    const res = await axios.post(`${BASE_URL}/coupons/`, body, { headers: authHeaders() });
    return res.data;
  } catch (e) { handle401(e); }
}

export async function updateAdminCoupon(couponId, patch) {
  try {
    const res = await axios.patch(`${BASE_URL}/coupons/${couponId}/`, patch, { headers: authHeaders() });
    return res.data;
  } catch (e) { handle401(e); }
}

export async function deleteAdminCoupon(couponId) {
  try {
    const res = await axios.delete(`${BASE_URL}/coupons/${couponId}/`, { headers: authHeaders() });
    return res.data;
  } catch (e) { handle401(e); }
}

// ---------- flyers (admin) ----------
// Lives at /api/flyer/ not /api/admin/flyer/ — keep using the original path.
const FLYER_BASE = `${API_BASE_URL}/api/flyer`;

export async function listFlyers() {
  try {
    const res = await axios.get(`${FLYER_BASE}/`, { headers: authHeaders() });
    return res.data;
  } catch (e) { handle401(e); }
}

export async function uploadFlyer({ image, store_name, valid_from, valid_until }) {
  const formData = new FormData();
  formData.append('image', image);
  formData.append('store_name', store_name);
  formData.append('valid_from', valid_from);
  formData.append('valid_until', valid_until);
  try {
    const res = await axios.post(`${FLYER_BASE}/upload/`, formData, {
      headers: authHeaders({ 'Content-Type': 'multipart/form-data' }),
    });
    return res.data;
  } catch (e) { handle401(e); }
}

export async function toggleFlyer(flyerId) {
  try {
    const res = await axios.patch(`${FLYER_BASE}/${flyerId}/toggle/`, {}, { headers: authHeaders() });
    return res.data;
  } catch (e) { handle401(e); }
}

export async function deleteFlyer(flyerId) {
  try {
    const res = await axios.delete(`${FLYER_BASE}/${flyerId}/delete/`, { headers: authHeaders() });
    return res.data;
  } catch (e) { handle401(e); }
}

// ---------- push broadcast ----------
export async function broadcastNotification(title, body) {
  try {
    const res = await axios.post(
      `${API_BASE_URL}/api/notifications/broadcast/`,
      { title, body },
      { headers: authHeaders() },
    );
    return res.data;
  } catch (e) { handle401(e); }
}
